import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, USER_COMMANDS } from 'cpool-constants';
import type { Schema_User } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { Adviser_Schema } from '../types/AdviserSchema';
import { buildAbsenceFromDate, buildAbsenceUntilDate } from '../utils/buildAbsenceDates';
import { APICommunicator } from '~/api/APICommunicator';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

type AdviserAbsence = {
    absenceFrom: Date;
    absenceUntil: Date;
    onSuccess: () => void;
};

export const useSetAdviserAbsenceMutation = ({ absenceFrom, absenceUntil, onSuccess }: AdviserAbsence) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const normalizedAbsenceFrom = buildAbsenceFromDate(absenceFrom);
    const normalizedAbsenceUntil = buildAbsenceUntilDate(absenceUntil);

    return useMutation({
        mutationFn: async ({ delegate, userId }: { userId?: string; delegate?: Adviser_Schema }) => {
            if (!userId) {
                throw new Error(t('DASHBOARD.ADVISER_ABSENCE.ERROR'));
            }

            if (!delegate) {
                throw new Error(t('DASHBOARD.ADVISER_ABSENCE.NO_DELEGATE_SELECTED'));
            }

            if (normalizedAbsenceFrom.getTime() > normalizedAbsenceUntil.getTime()) {
                throw new Error(t('DASHBOARD.ADVISER_ABSENCE.INVALID_DATE'));
            }

            return APICommunicator.sendCommand({
                aggregateId: userId,
                aggregateName: AGGREGATE_NAMES.USER,
                commandName: USER_COMMANDS.setAdviserAbsence,
                payload: {
                    absenceFrom: normalizedAbsenceFrom,
                    absenceUntil: normalizedAbsenceUntil,
                    delegateId: delegate.id,
                },
            });
        },
        onMutate: async ({ delegate, userId }) => {
            queryClient.setQueryData(queryKeys.getUser(userId), (data: Schema_User) => {
                return {
                    ...(data || {}),
                    absence: [
                        ...(data?.absence || []),
                        {
                            absenceFrom: normalizedAbsenceFrom,
                            absenceUntil: normalizedAbsenceUntil,
                            delegateId: delegate?.id,
                            eventId: '',
                            reason: '',
                        },
                    ],
                };
            });
        },
        onSettled: async (data, _, { userId }) => {
            if (data) setQueryPosition(JSON.stringify(queryKeys.getUser(userId)), data.position);
            await queryClient.invalidateQueries({ queryKey: queryKeys.getUser(userId) });
        },
        onSuccess: () => {
            onSuccess();
            toast.success(t('DASHBOARD.ADVISER_ABSENCE.SAVED'));
        },
    });
};
